import { withBusyCursor } from './busyCursor.js';
// securityUtil.js

import { API_URL } from "./config.js";

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";

function apiUrl(path = "") {
  const base = API_URL.endsWith("/") ? API_URL : `${API_URL}/`;
  const cleanPath = String(path).replace(/^\/+/, "");

  if (cleanPath.startsWith("api/") && base === "/api/") {
    return `/${cleanPath}`;
  }

  return `${base}${cleanPath}`;
}

function resolveFetchUrl(url) {
  if (typeof url !== "string") {
    return url;
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const base = API_URL.endsWith("/") ? API_URL : `${API_URL}/`;

  if (url.startsWith(base) || url.startsWith("/api/")) {
    return url;
  }

  if (url.startsWith("/")) {
    return apiUrl(url);
  }

  return url;
}

export function getAccessTokenFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get("auth"); // may be null
}

export function persistAccessToken(token) {
  if (token) {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  }
}


/* --------------------------------------------------
   Token storage
-------------------------------------------------- */

export function setTokens(accessToken, refreshToken) {

  if (!accessToken || !refreshToken) {
    console.warn("Attempted to store incomplete tokens", {
      accessToken,
      refreshToken
    });
    return;
  }

  sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function setAccessToken(accessToken) {

  if (!accessToken) {
    console.warn("Attempted to store incomplete tokens", {
      accessToken
    });
    return;
  }

  sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
}

export function getAccessToken() {
  return (
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("access_token")
  );
}


export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function clearTokens() {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}


/* --------------------------------------------------
   JWT helpers
-------------------------------------------------- */

function parseJwt(token) {
  try {
    const base64 = token.split(".")[1];
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function decodeJwtClaims(token) {
  if (!token || typeof token !== "string") {
    return null;
  }

  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch (e) {
    console.error("JWT decode failed", e);
    return null;
  }
}

export function getJwtUserId(claims) {
  if (!claims || typeof claims !== 'object') return null;
  return claims.userID || claims.userId || claims.user_id || claims.sub || null;
}

export function isJwtExpired(token) {
  try {
    if (!token) return true;
    const payload = decodeJwtClaims(token);
    if (!payload || !payload.exp) return true;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}


/* --------------------------------------------------
   Refresh JWT
-------------------------------------------------- */
let refreshInProgress = false;
let refreshPromise = null;

export async function refreshAccessToken() {

  if (refreshPromise) return refreshPromise;
  refreshInProgress = true;

  refreshPromise = (async () => {

  try
  {

    const refreshToken = getRefreshToken();

    //HARD STOP — this is CRITICAL
    if (!refreshToken || refreshToken === "undefined") {
        console.warn("Refresh token missing — refresh aborted");
        return false;
    }

    const resp = await fetch(apiUrl("auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!resp.ok) {
        clearTokens();
        return false;
    }

    const data = await resp.json();
    const accessToken = data.access_token || data.accessToken;
    const nextRefreshToken = data.refresh_token || data.refreshToken;

    if (!accessToken || !nextRefreshToken) {
      console.warn("Refresh response missing tokens", data);
      return false;
    }

    setTokens(accessToken, nextRefreshToken);
    return true;
  }

  finally 
  {
   refreshInProgress = false;
   refreshPromise = null;
  }


  })();

  return refreshPromise;
}

/* --------------------------------------------------
   Secure fetch (JWT auto-attach + auto-refresh)
-------------------------------------------------- */

export async function secureFetch(url, options = {}) {
  return withBusyCursor(() => executeSecureFetch(url, options));
}

async function executeSecureFetch(url, options = {}) {

  const token = getAccessToken();

  // If no token, do NOT refresh
  if (!token) {
    throw new Error("No access token available");
  }

  // Only refresh if token is expired
if (isJwtExpired(token)) {
  const refreshed = await refreshAccessToken();
  if (!refreshed) {

    console.warn("Refresh token missing — refresh aborted");
    clearTokens();
    throw new Error("Session expired");

  }
}


  const resolvedUrl = resolveFetchUrl(url);

  const doFetch = (bearerToken) => fetch(resolvedUrl, {
    ...options,
    headers: {
      ...(options.headers || {}),
      Authorization: `Bearer ${bearerToken}`,
      "Content-Type": "application/json"
    }
  });

  let response = await doFetch(getAccessToken());

  if (response.status === 401 || response.status === 403) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await doFetch(getAccessToken());
    }
  }

  return response;
}

export function isTokenExpired(decodedClaims, skewSeconds = 30) {
  if (!decodedClaims?.exp) return true;

  const now = Math.floor(Date.now() / 1000);
  return now >= (decodedClaims.exp - skewSeconds);
}



